/**
 * The all-regions roster's column model (BON-534).
 *
 * The roster is described once, as data, and the header cells, the body cells
 * and the CSS grid track list are all derived from the SAME filtered array. A
 * hidden column therefore cannot leave a track with no cell behind it — the
 * layout hole is structurally unavailable rather than something to remember.
 *
 * A CSS grid rather than a `<table>` because a parent row has to expand into
 * child rows while everything stays on one alignment, which a table cannot do
 * without nesting. The ARIA table roles are carried explicitly instead.
 *
 * The track list is tuned so the slack lands where it is READ: the counts are
 * three or four digits and need no room to grow, while the ticket fill is a bar
 * whose whole job is to be compared across rows. Region keeps a floor wide
 * enough for an indented third-level name, but no longer takes twice the
 * remaining space.
 */
import type { ReactNode } from 'react';

import { byName } from '../../lib/compare';
import { withoutUnbuilt, type UnbuiltFeature } from '../../lib/unbuilt-features';

import type { OverviewRegionRow } from './overview-types';

export { byName };

export type RosterColumnId =
  | 'region'
  | 'director'
  | 'venues'
  | 'workshops'
  | 'revenue'
  | 'tickets'
  | 'issues'
  | 'health';

export interface RosterColumn {
  id: RosterColumnId;
  label: string;
  /** One CSS grid track. */
  width: string;
  align?: 'right';
  /** Drawn quietly, so it does not compete with the names being scanned. */
  muted?: boolean;
  /** Present when the column depends on a feature that may not exist yet. */
  feature?: UnbuiltFeature;
  render: (row: OverviewRegionRow) => ReactNode;
}

export interface RosterSortOption {
  id: 'name' | 'revenue' | 'workshops' | 'issues';
  label: string;
  feature?: UnbuiltFeature;
  compare: (
    a: Pick<OverviewRegionRow, 'name' | 'revenueCents' | 'workshopCount'>,
    b: Pick<OverviewRegionRow, 'name' | 'revenueCents' | 'workshopCount'>,
  ) => number;
}

/** Whole dollars — a roster is scanned, not reconciled, so cents are noise. */
export const formatMoney = (cents: number): string =>
  `$${Math.round(cents / 100).toLocaleString('en-US')}`;

/** Sold against capacity, as a percentage. Zero capacity reads 0, not NaN. */
export const fillPercent = (sold: number, capacity: number): number =>
  capacity > 0 ? Math.round((sold / capacity) * 100) : 0;

/**
 * Every column the roster could show. `ROSTER_COLUMNS` below is what it does
 * show; the two differ only by which features exist. `region` renders nothing
 * here because its cell carries the expand control and the link, which the row
 * component owns.
 */
const ALL_COLUMNS: RosterColumn[] = [
  { id: 'region', label: 'Region', width: 'minmax(190px,1.3fr)', render: () => null },
  {
    id: 'director',
    label: 'Director',
    width: 'minmax(130px,0.9fr)',
    muted: true,
    render: (row) => row.directorName ?? '—',
  },
  {
    id: 'venues',
    label: 'Venues',
    width: '64px',
    align: 'right',
    render: (row) => row.venueCount,
  },
  {
    id: 'workshops',
    label: 'Workshops',
    width: '84px',
    align: 'right',
    render: (row) => row.workshopCount,
  },
  {
    id: 'revenue',
    label: 'Revenue',
    width: '104px',
    align: 'right',
    render: (row) => formatMoney(row.revenueCents),
  },
  { id: 'tickets', label: 'Tickets', width: 'minmax(170px,1.1fr)', render: () => null },
  {
    id: 'issues',
    label: 'Issues',
    width: '72px',
    align: 'right',
    feature: 'staffing-issues',
    render: () => null,
  },
  { id: 'health', label: 'Health', width: '96px', feature: 'region-health', render: () => null },
];

/**
 * FIRST is the default the page opens on, and that is Name: the figures answer
 * "how big is this territory", but the commonest reason to come here is to find
 * a region you can already name, and revenue order makes that a scan.
 */
const ALL_SORT_OPTIONS: RosterSortOption[] = [
  { id: 'name', label: 'Name', compare: byName },
  { id: 'revenue', label: 'Revenue', compare: (a, b) => b.revenueCents - a.revenueCents },
  { id: 'workshops', label: 'Workshops', compare: (a, b) => b.workshopCount - a.workshopCount },
  {
    id: 'issues',
    label: 'Issues',
    feature: 'staffing-issues',
    compare: () => 0,
  },
];

export const ROSTER_COLUMNS: RosterColumn[] = withoutUnbuilt(ALL_COLUMNS);
export const ROSTER_SORT_OPTIONS: RosterSortOption[] = withoutUnbuilt(ALL_SORT_OPTIONS);

/** The grid's track list — one track per visible column, in order. */
export const gridTemplateFor = (columns: readonly RosterColumn[]): string =>
  columns.map((column) => column.width).join(' ');
